import { useAuthStore } from '~/stores/auth'
import { getAccessToken } from '~/utils/authToken'
import { partnerMayOpen } from '~/navigation/nav'

const PUBLIC_PATHS = new Set([
  '/login',
  '/forgot-password',
  '/reset-password',
  '/accept-invite',
  '/not-authorized',
  '/change-password'
])

export default defineNuxtRouteMiddleware(async (to) => {
  if (import.meta.server) {
    return
  }

  const auth = useAuthStore()
  // A Door View's TV (task 2539) needs no person's log-in: its link carries the view's display key.
  const isPublicRoute = PUBLIC_PATHS.has(to.path) || to.path.startsWith('/door-board/tv/')

  if (!auth.isLoaded) {
    // Keep auth state warm without blocking the first paint.
    void auth.loadMe().catch((error) => {
      console.warn('[auth] Global warm-up failed:', error)
    })
  }

  if (isPublicRoute) {
    if (to.path === '/login') {
      // Avoid redirecting away from /login on stale in-memory auth state —
      // only redirect when auth has been validated this session.
      if (!auth.isLoaded && getAccessToken()) {
        try {
          await auth.loadMe()
        } catch (error) {
          console.warn('[auth] Login-route loadMe failed:', error)
        }
      }
      if (auth.isLoaded && auth.isAuthenticated) {
        return navigateTo('/')
      }
    }
    return
  }

  // A password someone else set (task 2459): the user sets their own before going anywhere.
  if (!auth.isLoaded && getAccessToken()) {
    try {
      await auth.loadMe()
    } catch (error) {
      console.warn('[auth] Password-check loadMe failed:', error)
    }
  }
  if (auth.isAuthenticated && auth.user?.mustChangePassword) {
    return navigateTo({ path: '/change-password', query: { to: to.fullPath } })
  }

  // A partner's own people (task 1309) open only the partner screens, Home and its dashboards among
  // them (Mark, 2026-10-20). The server refuses everything else anyway: this keeps a typed address
  // from landing on a screen that can only fail.
  if (auth.isAuthenticated && auth.isPartnerUser && !partnerMayOpen(to.path)) {
    return navigateTo('/not-authorized')
  }

  // Permission gate: routes can declare requiresAll / requiresAny in
  // definePageMeta. Authentication itself is enforced separately by the
  // named `auth` middleware on each protected page.
  const requiresAll = (to.meta.requiresAll ?? []) as string[]
  const requiresAny = (to.meta.requiresAny ?? []) as string[]

  if (requiresAll.length === 0 && requiresAny.length === 0) {
    return
  }

  // Need a loaded auth state to evaluate permissions. If we have a token but
  // haven't validated it yet, wait for /me to resolve.
  if (!auth.isLoaded && getAccessToken()) {
    try {
      await auth.loadMe()
    } catch (error) {
      console.warn('[auth] Permission-check loadMe failed:', error)
    }
  }

  // Not signed in — let the named auth middleware redirect to /login.
  if (!auth.isAuthenticated) {
    return
  }

  const passesAll = requiresAll.length === 0 || auth.hasAll(requiresAll)
  const passesAny = requiresAny.length === 0 || auth.hasAny(requiresAny)

  if (!passesAll || !passesAny) {
    return navigateTo('/not-authorized')
  }
})
